import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { AnalyticsContext } from '@/analytics/constants';
import { trackNotifyMeEvent } from '@/analytics/library';
import { isValidEmail, isValidName } from '@/utils/formHelpers';
import { useFetchAnalyticsItems } from '@/analytics/useFetchAnalyticsItems';
import useRecaptcha from '@/hooks/useRecaptcha';
import useToast from '@/hooks/useToast';
import { datadogRum } from '@/lib/DatadogService';
import { useSubmitNotifyMeMutation } from '@/redux/api/client/notification';
import { NotifyPanelItems } from '@/redux/slices/pdpSlice';
import { launchNotifyMePopup } from '@/utils/helpers';
import {
  NOTIFY_PANEL_ERROR_TOAST,
  NOTIFY_PANEL_RECAPTCHA_ACTION,
  NOTIFY_PANEL_SUCCESS_TOAST
} from '@/components/ui/PDP/ECommBox/constants';
import { isNativeNotifyMeSource } from '@/components/ui/PDP/ECommBox/notifyMeHelpers';

type UseNotifyMeSubmitProps = {
  notifyItems?: NotifyPanelItems;
  notifyMeUrl?: string;
  code?: string;
  context?: AnalyticsContext;
  onTrackNotifyMe?: () => void | Promise<void>;
  onSubmitted?: () => void;
};

const getNotifyMeResponseMessage = (value: unknown) => {
  if (!value || typeof value !== 'object') return undefined;

  const message = (value as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

const getNotifyMeErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) return error.message;

  if (!error || typeof error !== 'object') return undefined;

  const directMessage = getNotifyMeResponseMessage(error);
  if (directMessage) return directMessage;

  return getNotifyMeResponseMessage((error as { data?: unknown }).data);
};

export const useNotifyMeSubmit = ({
  notifyItems,
  notifyMeUrl,
  code,
  context,
  onTrackNotifyMe,
  onSubmitted
}: UseNotifyMeSubmitProps) => {
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSuccessMessage, showFailMessage } = useToast();
  const { executeRecaptcha, recaptchaLoaded } = useRecaptcha();
  const [submitNativeNotifyMe, { isLoading }] = useSubmitNotifyMeMutation();
  const { getAnalyticItem } = useFetchAnalyticsItems();
  const isNativeNotifyMe = isNativeNotifyMeSource(notifyItems?.source);
  const isGuestNativeNotifyMe = isNativeNotifyMe && !isAuthenticated;

  useEffect(() => {
    setSubmitted(false);
    setSubmitError(undefined);
    setIsSubmitting(false);
    setFirstName('');
    setLastName('');
    setEmail('');
  }, [code, notifyItems?.notifyMeType, isAuthenticated]);

  const trackNotifyMe = useCallback(async () => {
    if (onTrackNotifyMe) {
      await onTrackNotifyMe();
      return;
    }

    if (!code || !context) return;

    const data = await getAnalyticItem(code);
    if (data) {
      trackNotifyMeEvent(data, context);
    }
  }, [code, context, getAnalyticItem, onTrackNotifyMe]);

  const safeTrackNotifyMe = useCallback(() => {
    void trackNotifyMe().catch(error => {
      console.warn('[Notify Me] Failed to track notification request.', error);
      datadogRum.addError(
        error instanceof Error ? error : new Error('Notify Me tracking failed.'),
        {
          component: 'NotifyMeSubmit',
          feature: 'notify-me',
          programCode: code
        }
      );
    });
  }, [code, trackNotifyMe]);

  const handleLegacyClick = useCallback(async () => {
    const legacyUrl = notifyItems?.url ?? notifyMeUrl;
    if (!legacyUrl) return;

    safeTrackNotifyMe();
    launchNotifyMePopup(legacyUrl);
  }, [notifyItems?.url, notifyMeUrl, safeTrackNotifyMe]);

  const submitNotifyMe = useCallback(
    async (form?: HTMLFormElement | null) => {
      if (!isNativeNotifyMe) {
        await handleLegacyClick();
        return;
      }

      setSubmitError(undefined);

      if (!code || notifyItems?.notifyMeType === undefined) {
        datadogRum.addError(new Error('Notify Me submission missing required frontend data.'), {
          component: 'NotifyMeSubmit',
          feature: 'notify-me',
          hasCode: !!code,
          hasNotifyMeType: notifyItems?.notifyMeType !== undefined,
          notifyMeSource: notifyItems?.source,
          programCode: code
        });
        setSubmitError(NOTIFY_PANEL_ERROR_TOAST);
        showFailMessage(NOTIFY_PANEL_ERROR_TOAST);
        return;
      }

      const formData = form ? new FormData(form) : undefined;
      const requestFirstName = ((formData?.get('firstName') as string | null) ?? firstName).trim();
      const requestLastName = ((formData?.get('lastName') as string | null) ?? lastName).trim();
      const requestEmail = ((formData?.get('email') as string | null) ?? email).trim();

      if (isGuestNativeNotifyMe) {
        const validationMessage =
          !requestEmail ||
          !requestFirstName ||
          !requestLastName ||
          !isValidName(requestFirstName) ||
          !isValidName(requestLastName)
            ? 'Please complete all required fields.'
            : !isValidEmail(requestEmail)
              ? 'Please enter a valid email address.'
              : undefined;

        if (validationMessage) {
          setSubmitError(validationMessage);
          return;
        }
      }

      try {
        setIsSubmitting(true);

        if (!recaptchaLoaded) {
          console.warn(
            '[Notify Me] reCAPTCHA is not executable yet. Attempting execution with timeout.'
          );
        }

        const captchaInputText = await executeRecaptcha(NOTIFY_PANEL_RECAPTCHA_ACTION);
        const notifyMeRequest = {
          code,
          notifyMeType: notifyItems.notifyMeType,
          ...(isGuestNativeNotifyMe && {
            firstName: requestFirstName,
            lastName: requestLastName,
            email: requestEmail
          }),
          captchaInputText,
          captchaAction: NOTIFY_PANEL_RECAPTCHA_ACTION
        } as Parameters<typeof submitNativeNotifyMe>[0];

        const response = await submitNativeNotifyMe(notifyMeRequest).unwrap();

        if (response?.success === false) {
          throw new Error(response.message || NOTIFY_PANEL_ERROR_TOAST);
        }

        safeTrackNotifyMe();
        setSubmitted(true);
        onSubmitted?.();
        showSuccessMessage(NOTIFY_PANEL_SUCCESS_TOAST);
      } catch (error) {
        const errorMessage = getNotifyMeErrorMessage(error) ?? NOTIFY_PANEL_ERROR_TOAST;
        console.warn('[Notify Me] Failed to submit notification request.', error);
        datadogRum.addError(
          error instanceof Error ? error : new Error('Notify Me submission failed.'),
          {
            component: 'NotifyMeSubmit',
            feature: 'notify-me',
            programCode: code,
            recaptchaLoaded
          }
        );
        setSubmitError(errorMessage);
        showFailMessage(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      code,
      email,
      executeRecaptcha,
      firstName,
      handleLegacyClick,
      isGuestNativeNotifyMe,
      isNativeNotifyMe,
      lastName,
      notifyItems?.source,
      notifyItems?.notifyMeType,
      onSubmitted,
      recaptchaLoaded,
      safeTrackNotifyMe,
      showFailMessage,
      showSuccessMessage,
      submitNativeNotifyMe
    ]
  );

  return {
    email,
    firstName,
    isGuestNativeNotifyMe,
    isLoading,
    isNativeNotifyMe,
    isSubmitting,
    lastName,
    setEmail,
    setFirstName,
    setLastName,
    submitError,
    submitted,
    submitNotifyMe
  } as const;
};

export default useNotifyMeSubmit;
